'use client';
import React from 'react';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { Box, Button, Card, IconButton, Typography } from '@mui/material';

import { useEventBookmarkAction } from '@/hooks';
import { SportProp } from '@/types';

interface SportPropsCardProps {
  prop: SportProp;
  sportSlug: string;
  onTradeClick?: (propId: string, outcomeName: string, isYes: boolean) => void;
}

export const SportPropsCard = ({ prop, sportSlug, onTradeClick }: SportPropsCardProps) => {
  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();
  const isBookmarked = checkIsBookmarked(prop.id);

  const handleBookmarkClick = (e: React.MouseEvent) => {
    handleBookmarkToggle(e, prop as any, 'sport');
  };

  const handleYesClick = (outcomeName: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onTradeClick?.(prop.id, outcomeName, true);
  };

  const handleNoClick = (outcomeName: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onTradeClick?.(prop.id, outcomeName, false);
  };

  const getIcon = () => {
    switch (prop.icon) {
      case 'trophy':
        return '🏆';
      case 'football':
        return '🏈';
      case 'basketball':
        return '🏀';
      case 'baseball':
        return '⚾';
      case 'soccer':
        return '⚽';
      default:
        return '🏆';
    }
  };

  return (
    <Card
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        p: 1.5,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Header - Icon and Title */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '8px',
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '1.5rem',
          }}
        >
          {getIcon()}
        </Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.9375rem',
            color: 'text.primary',
            flex: 1,
            lineHeight: 1.3,
          }}
        >
          {prop.title}
        </Typography>
      </Box>

      {/* Outcomes */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
        {prop.outcomes.map((outcome, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.primary',
                flex: 1,
              }}
            >
              {outcome.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.primary',
                mr: 1,
                flexShrink: 0,
              }}
            >
              {outcome.percentage}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleYesClick(outcome.name)}
                sx={{
                  minWidth: 44,
                  px: 1,
                  py: 0.375,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  bgcolor: '#dcfce7',
                  color: '#166534',
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#bbf7d0',
                    boxShadow: 'none',
                  },
                }}
              >
                Yes
              </Button>
              <Button
                variant="contained"
                onClick={handleNoClick(outcome.name)}
                sx={{
                  minWidth: 44,
                  px: 1,
                  py: 0.375,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  bgcolor: '#fee2e2',
                  color: '#991b1b',
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#fecaca',
                    boxShadow: 'none',
                  },
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Footer - Volume and Bookmark */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {prop.volume}
        </Typography>
        <IconButton
          size="small"
          onClick={handleBookmarkClick}
          sx={{
            color: isBookmarked ? 'primary.main' : 'text.secondary',
            p: 0.5,
          }}
        >
          {isBookmarked ? (
            <BookmarkIcon sx={{ fontSize: '1rem' }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: '1rem' }} />
          )}
        </IconButton>
      </Box>
    </Card>
  );
};
